import React, { useEffect, useState } from 'react';
import { Card, ProgressBar } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faSearch } from '@fortawesome/free-solid-svg-icons';
import { fetchRiskStatistics } from 'services/hvarisk.service';

interface RiskRatioMiniChartProps {
  dateRange: {
    datefrom: string;
    dateto: string;
  };
  campaignId?: number;
}

const RiskRatioMiniChart: React.FC<RiskRatioMiniChartProps> = ({
  dateRange,
  campaignId
}) => {
  const [detected, setDetected] = useState(0);
  const [reviewed, setReviewed] = useState(0);
  const [ratio, setRatio] = useState(0);
  const [avgReviewTime, setAvgReviewTime] = useState(0);

  useEffect(() => {
    const loadData = async () => {
      try {
        const stats = await fetchRiskStatistics({
          datefrom: dateRange.datefrom,
          dateto: dateRange.dateto,
          campaignid: campaignId
        });

        if (stats && stats.length > 0) {
          const stat = stats[0];

          // Solo contamos riesgos con severidad >= 4 (críticos y altos)
          const totalDetected = stat.highSeverityRisks || 0;
          const pendingHighSeverity = stat.pendingReviews || 0;
          const totalReviewed = totalDetected - pendingHighSeverity;

          const reviewRatio =
            totalDetected > 0 ? (totalReviewed / totalDetected) * 100 : 0;

          setDetected(totalDetected);
          setReviewed(totalReviewed);
          setRatio(reviewRatio);
          setAvgReviewTime(stat.avgReviewTime || 0);
        }
      } catch (error) {
        console.error('Error loading ratio data:', error);
      }
    };

    loadData();
  }, [dateRange, campaignId]);

  const getProgressVariant = () => {
    if (ratio >= 80) return 'success';
    if (ratio >= 50) return 'warning';
    return 'danger';
  };

  const formatReviewTime = (minutes: number) => {
    if (minutes < 60) return `${Math.round(minutes)}m`;
    if (minutes < 1440) return `${(minutes / 60).toFixed(1)}h`;
    return `${(minutes / 1440).toFixed(1)}d`;
  };

  return (
    <Card className="h-100">
      <Card.Body className="p-3 pb-0">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <h6 className="mb-1 text-body-emphasis">
              Detection vs Review Ratio
            </h6>
            <div className="d-flex align-items-center gap-4">
              <div>
                <div className="d-flex align-items-center text-body-tertiary">
                  <FontAwesomeIcon icon={faSearch} className="me-2 fs-9" />
                  <span className="fs-9">Detected</span>
                </div>
                <h4 className="mb-0">{detected}</h4>
              </div>
              <div>
                <div className="d-flex align-items-center text-body-tertiary">
                  <FontAwesomeIcon icon={faCheckCircle} className="me-2 fs-9" />
                  <span className="fs-9">Reviewed</span>
                </div>
                <h4 className="mb-0">{reviewed}</h4>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-0">
          <div className="d-flex justify-content-between align-items-center mb-0">
            <span className="fs-10 text-body-tertiary">Review Progress</span>
            <span className="fs-9 fw-semibold">{ratio.toFixed(1)}%</span>
          </div>
          <ProgressBar
            now={ratio}
            variant={getProgressVariant()}
            style={{ height: '8px' }}
          />
          <div className="d-flex justify-content-between align-items-center mt-2 pt-0">
            <span className="fs-10 text-body-tertiary">Avg. Review Time</span>
            <span className="fs-9 fw-semibold text-body-emphasis">
              {formatReviewTime(avgReviewTime)}
            </span>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default RiskRatioMiniChart;
